import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, DollarSign, ArrowUpRight, ArrowDownRight,
  ChevronRight, Loader2, AlertTriangle, BarChart3, Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import CashFlowCard from '@/components/dashboard/CashFlowCard';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CashFlowWidget({ darkMode = false, compact = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  
  useEffect(() => {
    fetchCashFlow();
  }, []);
  
  const fetchCashFlow = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/financials/cash-flow?months_ahead=6`);
      if (res.data.success) {
        setData(res.data);
      }
    } catch (err) {
      console.error('Error fetching cash flow:', err);
      setError('Failed to load cash flow data');
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0';
    const absAmount = Math.abs(amount);
    if (absAmount >= 1000000) {
      return `${amount < 0 ? '-' : ''}$${(absAmount / 1000000).toFixed(1)}M`;
    }
    if (absAmount >= 1000) {
      return `${amount < 0 ? '-' : ''}$${(absAmount / 1000).toFixed(1)}K`;
    }
    return `${amount < 0 ? '-' : ''}$${absAmount.toFixed(0)}`;
  };
  
  const formatFullCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  if (compact) {
    // My Desk rendering — DESIGN PASS 8 second slice, on the token system.
    return (
      <CashFlowCard
        data={data}
        loading={loading}
        error={error}
        formatCurrency={formatCurrency}
        onOpen={() => navigate('/financials')}
      />
    );
  }

  if (loading) {
    return (
      <Card className={`${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }
  
  if (error || !data) {
    return (
      <Card className={`${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
        <CardContent className="p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{error || 'No data available'}</p>
        </CardContent>
      </Card>
    );
  }
  
  const { summary, monthly } = data;
  const currentMonth = monthly[0];
  const nextMonth = monthly[1];
  
  // Calculate trend
  const currentNet = currentMonth?.net_cash_flow || 0;
  const nextNet = nextMonth?.net_cash_flow || 0;
  const trend = nextNet - currentNet;
  const trendPercent = currentNet !== 0 ? ((trend / Math.abs(currentNet)) * 100) : 0;
  
  // Determine health status
  const cashPosition = summary.projected_cash_position;
  const healthStatus = cashPosition > 50000 ? 'healthy' : cashPosition > 0 ? 'caution' : 'critical';
  const healthColors = {
    healthy: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
    caution: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
    critical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' }
  };
  
  // Full widget view
  return (
    <Card className={`${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className={`text-base flex items-center gap-2 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
            <BarChart3 className="w-5 h-5 text-[#70170F]" />
            Cash Flow Forecast
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs"
            onClick={() => navigate('/financials')}
          >
            View Details <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-green-50'} border ${darkMode ? 'border-[#2A2A2A]' : 'border-green-100'}`}>
                  <div className="flex items-center gap-1 mb-1">
                    <ArrowUpRight className="w-3 h-3 text-green-600" />
                    <span className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>Money In</span>
                  </div>
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(summary.outstanding_receivables)}
                  </p>
                  <p className={`text-[10px] ${darkMode ? 'text-[#6A6A6A]' : 'text-gray-400'}`}>
                    {data.breakdown.pending_invoices_count} pending invoices
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Outstanding receivables from sent invoices</p>
                <p className="font-semibold">{formatFullCurrency(summary.outstanding_receivables)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-red-50'} border ${darkMode ? 'border-[#2A2A2A]' : 'border-red-100'}`}>
                  <div className="flex items-center gap-1 mb-1">
                    <ArrowDownRight className="w-3 h-3 text-red-600" />
                    <span className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>Money Out</span>
                  </div>
                  <p className="text-lg font-bold text-red-600">
                    {formatCurrency(summary.outstanding_payables + summary.committed_purchase_orders)}
                  </p>
                  <p className={`text-[10px] ${darkMode ? 'text-[#6A6A6A]' : 'text-gray-400'}`}>
                    Payables + POs
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Vendor invoices: {formatFullCurrency(summary.outstanding_payables)}</p>
                <p>Purchase orders: {formatFullCurrency(summary.committed_purchase_orders)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={`p-3 rounded-lg ${healthColors[healthStatus].bg} border ${healthColors[healthStatus].border}`}>
                  <div className="flex items-center gap-1 mb-1">
                    <Wallet className={`w-3 h-3 ${healthColors[healthStatus].text}`} />
                    <span className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>Net Position</span>
                  </div>
                  <p className={`text-lg font-bold ${healthColors[healthStatus].text}`}>
                    {formatCurrency(cashPosition)}
                  </p>
                  <p className={`text-[10px] ${darkMode ? 'text-[#6A6A6A]' : 'text-gray-400'}`}>
                    {healthStatus === 'healthy' ? 'Looking good' : healthStatus === 'caution' ? 'Monitor closely' : 'Needs attention'}
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Projected position after all pending transactions</p>
                <p className="font-semibold">{formatFullCurrency(cashPosition)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {/* Monthly Trend Mini Chart */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-xs font-medium ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>
              6-Month Forecast
            </span>
            {trend !== 0 && (
              <div className={`flex items-center gap-1 text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {Math.abs(trendPercent).toFixed(0)}%
              </div>
            )}
          </div>
          
          {/* Simple bar chart */}
          <div className="flex items-end gap-1 h-16">
            {monthly.slice(0, 6).map((month, idx) => {
              const maxValue = Math.max(...monthly.slice(0, 6).map(m => Math.abs(m.net_cash_flow))) || 1;
              const height = Math.max(10, (Math.abs(month.net_cash_flow) / maxValue) * 100);
              const isPositive = month.net_cash_flow >= 0;
              
              return (
                <TooltipProvider key={month.month}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex-1 flex flex-col items-center">
                        <div 
                          className={`w-full rounded-t transition-all ${
                            isPositive 
                              ? 'bg-green-500' 
                              : 'bg-red-500'
                          } ${idx === 0 ? 'opacity-100' : 'opacity-70'}`}
                          style={{ height: `${height}%`, minHeight: '4px' }}
                        />
                        <span className={`text-[9px] mt-1 ${darkMode ? 'text-[#6A6A6A]' : 'text-gray-400'}`}>
                          {month.month_label.slice(0, 3)}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">{month.month_label}</p>
                      <p className={isPositive ? 'text-green-600' : 'text-red-600'}>
                        Net: {formatFullCurrency(month.net_cash_flow)}
                      </p>
                      <p className="text-xs text-gray-500">
                        In: {formatFullCurrency(month.inflows.total)} | Out: {formatFullCurrency(month.outflows.total)}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className={`pt-3 border-t ${darkMode ? 'border-[#2A2A2A]' : ''} grid grid-cols-2 gap-4 text-xs`}>
          <div>
            <span className={`${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>This Month</span>
            <p className={`font-semibold ${currentNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(currentNet)}
            </p>
          </div>
          <div>
            <span className={`${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>Next Month</span>
            <p className={`font-semibold ${nextNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(nextNet)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
